import { Fragment } from 'react';
import { Tooltip } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fillTemplate, relativeDateLabel, tidyTemplateLabel } from './utils/popupUtils';

// ── Rich dropdown rows ───────────────────────────────────────────────────────
// A search-select can render each dropdown row as a multi-line card instead of
// a single label string, driven entirely by a declarative `field.optionLayout`
// so popup DEFINITIONS (plain .js config files) stay JSX-free:
//
//   optionLayout: {
//     id:       '{applicantRefrenceId}',            // small mono chip, optional
//     title:    '{firstName} {lastName}',           // primary line
//     subtitle: '{designation}',                    // secondary line, optional
//     meta: [                                       // footer key/value pairs
//       { label: 'Created', key: 'createdAt', type: 'date' },
//       { label: 'Updated', key: 'updatedAt', type: 'relative' },
//     ],
//   }
//
// Every string is a `{placeholder}` template resolved against the raw API row.
// The already-submitted tick is NOT part of the layout config — it is derived
// from the option's own `alreadySubmitted` / `submittedAt` (see
// popupUtils.normalizeOption), so it works for any module that flags rows.

const ABSOLUTE_DATE_FORMAT = 'DD MMM YYYY';
const FULL_DATE_FORMAT = 'DD MMM YYYY, h:mm A';

function formatOptionDate(value, type) {
  if (value === undefined || value === null || value === '') return '';
  const date = dayjs(value);
  if (!date.isValid()) return String(value);
  // `relative` uses the popup's own Today / Yesterday / N days ago labels —
  // dayjs's fromNow() needs the relativeTime plugin, which this package does
  // not register.
  return type === 'relative'
    ? relativeDateLabel(value) || date.format(ABSOLUTE_DATE_FORMAT)
    : date.format(ABSOLUTE_DATE_FORMAT);
}

/** Resolves one `optionLayout` slot: a `{template}` string, or a plain key.
 *  Runs through tidyTemplateLabel so a slot like '{designation} · {email}' does
 *  not render a dangling '·' when the candidate has no designation. */
function resolveSlot(template, row) {
  if (!template) return '';
  if (!String(template).includes('{')) return String(row?.[template] ?? template).trim();
  return tidyTemplateLabel(fillTemplate(template, row));
}

/** The already-submitted marker: a LEADING tick, no text.
 *
 *  This was a right-rail "Submitted" badge in a reserved 100px column. The
 *  column had to be reserved so the dates could not shift between a flagged
 *  and an unflagged row — which meant every selectable row carried 100px of
 *  blank space to the right of its dates. Moving the marker to a 16px gutter
 *  before the name keeps every title aligned at a fraction of the cost, and
 *  reads naturally: the tick qualifies the person, so it belongs beside them.
 *  The word "Submitted" is redundant next to a green tick — the date it stood
 *  for is richer, and now lives in the tooltip.
 */
/** The tick marks SUBMISSION HISTORY, in two flavours.
 *
 *  isFromSubmission — the candidate has a submission to some OTHER job. That
 *    never blocks: they are a perfectly valid pick, and the prior submission is
 *    what the form prefills from. A green tick reads as "we know this person".
 *
 *  alreadySubmitted — a submission to THIS job. Cannot be picked again.
 *    The tick goes muted, because a green tick on an unpickable row reads as
 *    approval; the reason itself is carried by the rail (see OptionDates).
 *
 *  Both are history, so both get the tick — what differs is its tone and what
 *  the tooltip says. */
function tickState(option) {
  if (option?.alreadySubmitted) {
    const when = option?.submittedAt ? dayjs(option.submittedAt) : null;
    return {
      tone: 'blocked',
      tooltip: when?.isValid()
        ? `Already submitted to this job on ${when.format(FULL_DATE_FORMAT)}`
        : 'Already submitted to this job — a candidate can only be submitted once per job',
    };
  }
  if (option?.isFromSubmission) {
    const when = option?.priorSubmittedAt ? dayjs(option.priorSubmittedAt) : null;
    return {
      tone: 'history',
      tooltip: when?.isValid()
        ? `Previously submitted on ${when.format(FULL_DATE_FORMAT)} — this form will prefill from it`
        : 'Submitted to another job before — this form will prefill from it',
    };
  }
  return null;
}

/** Why a row is unselectable for a reason OTHER than being already submitted
 *  (e.g. a fulfilled job). Rendered inline after the title rather than in a
 *  rail, so it costs nothing on the rows that do not have one. */
function blockedNote(option, row) {
  if (option?.alreadySubmitted || !option?.disabled) return '';
  return String(row?.message ?? option?.title ?? '').trim();
}

/** The dates rail.
 *
 *  A two-column GRID, with the label and the value as direct grid children —
 *  not one right-aligned span per date. When each date was a single span, the
 *  labels floated: "Created 28 Jul 2026" and "Updated Today" are different
 *  widths, so "Created" and "Updated" landed at different x positions and the
 *  pair never read as a column. As a grid the labels share column 1 and the
 *  values share a right-aligned column 2, so both align down the row AND
 *  across every row in the list (the label column is `max-content`, identical
 *  for identical labels). */
function OptionDates({ meta }) {
  return (
    <div className="dynamic-popup__option-dates">
      {meta.map((entry) => (
        <Fragment key={entry.label}>
          <span className="dynamic-popup__option-date-label">{entry.label}</span>
          <span className="dynamic-popup__option-date-value">{entry.text}</span>
        </Fragment>
      ))}
    </div>
  );
}

function RichSelectOption({ option, layout }) {
  const row = option?._raw ?? option ?? {};
  const id = resolveSlot(layout.id, row);
  const title = resolveSlot(layout.title, row) || String(option?.label ?? '');
  const subtitle = resolveSlot(layout.subtitle, row);
  const emphasisText = resolveSlot(layout.titleAccent?.key, row);
  const emphasisTone = resolveSlot(layout.titleAccent?.toneKey, row);
  const emphasisPrefix = layout.titleAccent?.prefix ?? '';
  const meta = (layout.meta ?? [])
    .map((entry) => ({ label: entry.label, text: formatOptionDate(row?.[entry.key], entry.type) }))
    .filter((entry) => entry.text);
  const note = blockedNote(option, row);
  const tick = tickState(option);

  // Two columns: the leading tick gutter + identity (grows), then the dates
  // rail. The gutter is always reserved — 16px — so titles line up whether or
  // not a row is flagged, without the 100px of dead space the old right-hand
  // status column cost every unflagged row.
  return (
    <div className="dynamic-popup__option">
      <span className={`dynamic-popup__option-tick${tick ? ` dynamic-popup__option-tick--${tick.tone}` : ''}`}>
        {tick && (
          <Tooltip title={tick.tooltip} placement="topLeft">
            <CheckCircleFilled aria-label={tick.tooltip} />
          </Tooltip>
        )}
      </span>
      <div className="dynamic-popup__option-main">
        <div className="dynamic-popup__option-head">
          {id && <span className="dynamic-popup__option-id">{id}</span>}
          <span className="dynamic-popup__option-title">
            <span className="dynamic-popup__option-title-text">{title}</span>
            {emphasisText && (
              <span className={`dynamic-popup__option-title-accent${emphasisTone ? ` dynamic-popup__option-title-accent--${emphasisTone}` : ''}`}>
                {`${emphasisPrefix}${emphasisText}`}
              </span>
            )}
          </span>
          {note && <span className="dynamic-popup__option-flag">{note}</span>}
        </div>
        {subtitle && <div className="dynamic-popup__option-sub">{subtitle}</div>}
      </div>
      {option?.alreadySubmitted
        ? <span className="dynamic-popup__option-onjob">On this job</span>
        : meta.length > 0 && <OptionDates meta={meta} />}
    </div>
  );
}

export default RichSelectOption;
